import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircle } from "lucide-react";
import { TALENT_CART_PROMPT_EVENT } from "../hooks/useTalentCart";
import { useLanguage } from "../../context/LanguageContext";

// On the casting-brief form itself, picking a talent is already the point of
// the page — a full-screen "keep browsing or review & submit?" modal just
// interrupts selecting several talents in a row. Show a quiet toast instead.
const isCastingBriefRoute = (pathname: string) => pathname.startsWith("/casting-brief");

export default function TalentCartPrompt() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const [talentName, setTalentName] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ name?: string }>).detail;
      const name = detail?.name || t("talentCartPrompt.thisTalent");

      if (isCastingBriefRoute(location.pathname)) {
        setToast(t("talentCartPrompt.addedToBrief", { name }));
        if (toastTimer.current) clearTimeout(toastTimer.current);
        toastTimer.current = setTimeout(() => setToast(null), 2200);
        return;
      }

      setTalentName(name);
    };
    window.addEventListener(TALENT_CART_PROMPT_EVENT, handler);
    return () => {
      window.removeEventListener(TALENT_CART_PROMPT_EVENT, handler);
      if (toastTimer.current) clearTimeout(toastTimer.current);
    };
  }, [location.pathname, t]);

  if (toast) {
    return (
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] bg-[#313131] text-white px-5 py-3 rounded-full shadow-xl flex items-center gap-2 text-sm font-medium">
        <CheckCircle className="text-[#b3d130]" size={18} />
        {toast}
      </div>
    );
  }

  if (!talentName) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60]">
      <div className="bg-white rounded-2xl max-w-sm w-full p-6 text-center shadow-xl">
        <div className="w-12 h-12 bg-[#b3d130]/15 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="text-[#b3d130]" size={28} />
        </div>
        <h3 className="text-lg font-bold text-[#313131] mb-1">{t("talentCartPrompt.addedToBrief", { name: talentName })}</h3>
        <p className="text-sm text-gray-500 mb-6">{t("talentCartPrompt.continueBrowsing")}</p>
        <div className="flex flex-col gap-2">
          <button
            onClick={() => setTalentName(null)}
            className="w-full bg-[#b3d130] hover:bg-[#a5c428] text-[#313131] font-semibold py-2.5 rounded-full transition-colors"
          >
            {t("talentCartPrompt.keepBrowsing")}
          </button>
          <button
            onClick={() => {
              setTalentName(null);
              navigate("/casting-brief");
            }}
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2.5 rounded-full transition-colors"
          >
            {t("talentCartPrompt.reviewAndSubmit")}
          </button>
        </div>
      </div>
    </div>
  );
}
